import { DialogComponent } from '@theme/dialog';
import { createVtoAdapter, knownWithout, probeRetryIn, uidsWith } from '@theme/vto-adapter';

/**
 * Shared Fittingbox virtual try-on dialog. Rendered once per page; opened by any
 * `[data-vto-launch]` trigger and hydrated from that launcher's JSON payload, so PLP cards
 * only ship the small launcher rather than a full dialog each.
 *
 * @typedef {object} VtoVariant
 * @property {string|number} id
 * @property {string} colorLabel  Bare frame-color name (e.g. "Nero"); reused for the dialog header.
 * @property {string} [swatchLabel]  Frame+lens summary (e.g. "Frame Nero, Lens G-15 verde") for the swatch text.
 * @property {string} [colorCode]
 * @property {string[]} [frameIds]  Candidate Fittingbox ids (barcode, sku_supplier, "<sku_supplier>-<color_code>", and the same without a trailing "/1" on sku_supplier).
 * @property {string|null} [frameId]  The candidate Fittingbox confirmed available; set during resolution.
 * @property {string} [sku]
 * @property {string} [swatchImage]
 * @property {string} [heroImage]
 * @property {boolean} available
 *
 * @typedef {object} VtoPayload
 * @property {string} [brand]
 * @property {string} [modelLine]
 * @property {string|number} [productId]
 * @property {string} [url]
 * @property {string} [image]
 * @property {VtoVariant[]} variants
 *
 * @typedef {import('@theme/dialog').DialogComponent & { refs: Record<string, HTMLElement | undefined> }} _
 * @extends DialogComponent
 */
class VtoDialog extends DialogComponent {
  requiredRefs = ['dialog'];

  /** @type {ReturnType<typeof createVtoAdapter> | null} */
  #adapter = null;
  /** @type {Promise<unknown>} */
  #frameQueue = Promise.resolve();
  /** @type {VtoVariant[]} */
  #variants = [];
  /** Base model line (e.g. "0DG4501"); the per-variant color segment is appended on selection. */
  #modelLine = '';
  /** @type {import('@theme/vto-adapter').VtoMode} Which experience the current open is showing. */
  #mode = 'live';
  /** @type {Map<string, string>} Carousel frame id → that colour's PDP, for "View product". */
  #productUrls = new Map();
  /** Bumped by every open and close, so an open still awaiting its lookups knows it went stale. */
  #openSeq = 0;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('dialog:close', this.#onClose);
    this.refs.swatches?.addEventListener('click', this.#onSwatchClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#adapter?.destroy();
  }

  /**
   * Hydrates and opens the dialog.
   * @param {VtoPayload} payload
   * @param {import('@theme/vto-adapter').VtoMode} [mode] Which launcher opened it (VIS-599).
   */
  async open(payload, mode = 'live') {
    const seq = ++this.#openSeq;
    this.#mode = mode;
    this.dataset.mode = mode;
    this.removeAttribute('data-live');
    this.removeAttribute('data-carousel');
    // Diagnostics are per-session: a stale value from the previous open would misreport this one.
    delete this.dataset.removal;
    delete this.dataset.issue;
    this.#hydrate(payload);
    // Show a loading state immediately (responsive to the click) while we resolve which Fittingbox
    // id each variant loads under — probing the viewer's own findByApiKey lookup — before committing
    // to a state, so we never flash the empty camera panel for a frame that turns out to be
    // unavailable, nor auto-start the try-on on a frame Fittingbox can't load.
    this.#setState('loading');
    this.showDialog();

    // The carousel's related frames load alongside, against a deadline counted from the click: a
    // late answer means no carousel this time rather than one that reflows a running session.
    const deadline = new Promise((resolve) => setTimeout(() => resolve([]), RELATED_DEADLINE_MS));
    const related = Promise.race([this.#fetchRelated(payload.productId).catch(() => []), deadline]);

    const anyAvailable = await this.#resolveAvailability();
    if (seq !== this.#openSeq) return; // closed, or another product opened, meanwhile
    // No usable frame → show the unavailable message instead of booting into a broken widget.
    if (!anyAvailable) {
      this.#setState('unavailable');
      return;
    }

    const initial = this.#initialVariant();
    const relatedFrames = await related;
    if (seq !== this.#openSeq) return;
    const carousel = this.#buildCarousel(payload, initial, relatedFrames);
    this.toggleAttribute('data-carousel', Boolean(carousel));
    // Before the frame: a different carousel recreates the widget, around the pending frame.
    this.#ensureAdapter().setCarousel(carousel);
    this.#setState('active');
    // A fresh queue: this open's frame must reach the widget before it is created around a stale one.
    this.#frameQueue = Promise.resolve();
    if (initial) this.#selectVariant(String(initial.id));
    // The Fittingbox widget renders its own (branded) privacy consent + camera-permission UI inside
    // the viewer, so the user sees a single consent screen.
    this.#start();
  }

  /** Starts the try-on. The widget handles its own privacy consent + camera permission in-iframe. */
  #start = async () => {
    try {
      await this.#ensureAdapter().startVto(this.#mode);
    } catch {
      /* the widget handles consent/permission denial inside the iframe; mock failure is dev-only */
    }
  };

  /** Saves the current try-on frame (mock: downloads a snapshot; real: SDK capture). */
  save = () => {
    this.#ensureAdapter().capture();
  };

  /** Restarts the try-on session. */
  restart = async () => {
    try {
      await this.#ensureAdapter().restart();
    } catch {
      /* the widget surfaces restart / permission errors in its own in-iframe UI */
    }
  };

  #onClose = () => {
    this.#openSeq += 1;
    this.#adapter?.stopVto();
    this.removeAttribute('data-live');
  };

  /** @param {Event} event */
  #onSwatchClick = (event) => {
    const button = /** @type {HTMLElement} */ (event.target).closest('[data-variant-id]');
    if (!(button instanceof HTMLElement) || button.getAttribute('aria-disabled') === 'true') return;
    this.#selectVariant(button.dataset.variantId);
  };

  #ensureAdapter() {
    if (!this.#adapter) {
      const container = this.refs.viewer;
      this.#adapter = createVtoAdapter({
        container: container instanceof HTMLElement ? container : this,
        apiKey: this.dataset.apiKey,
      });
      // User declined the in-widget privacy consent → close the whole dialog.
      this.#adapter.on('declined', () => this.closeDialog());
      // Reveal the save/restart controls only once the virtual frame is actually on the face.
      this.#adapter.on('live', (live) => this.toggleAttribute('data-live', Boolean(live)));
      // Whether Frame Removal engaged, and whatever the widget is complaining about (see
      // onLiveStatus / onIssue in vto-adapter.js for why neither is knowable any other way).
      // Reflected as attributes rather than logged: readable in production DevTools at any moment,
      // with no console output for shoppers. With the dialog open on a PDP,
      // `document.getElementById('vto-dialog').dataset` reads:
      //   { state:"active", live:"", removal:"on"  }  removal engaged
      //   { state:"active", live:"", removal:"off" }  gate not met (eyewear not OPTICAL, or pose)
      //   { ..., removal:"off", issue:"removalLowPerformances" }  device too slow
      //   { state:"active" }  onLiveStatus never fired, so the widget never started
      // An ABSENT attribute is therefore a different diagnosis from "off". See VIS-624.
      this.#adapter.on('removal', (active) => {
        this.dataset.removal = active ? 'on' : 'off';
      });
      this.#adapter.on('issue', (issues) => {
        const list = /** @type {string[]} */ (issues).join(' ');
        if (list) this.dataset.issue = list;
        else delete this.dataset.issue;
      });
      // The carousel's "View product" button, on whichever frame and colour is on screen.
      this.#adapter.on('view-product', (data) => {
        const url = this.#productUrls.get(String(/** @type {any} */ (data)?.frameId));
        if (url) location.assign(url);
      });
    }
    return this.#adapter;
  }

  /** @param {VtoPayload} payload */
  #hydrate(payload) {
    this.#variants = payload.variants ?? [];
    this.#modelLine = payload.modelLine ?? '';
    if (this.refs.brand instanceof HTMLElement) this.refs.brand.textContent = payload.brand ?? '';
    if (this.refs.model instanceof HTMLElement) this.refs.model.textContent = this.#modelLine;
    this.#renderSwatches();
  }

  /**
   * Resolves each variant's Fittingbox frame id from its candidate `frameIds`, disables the swatches
   * that have no loadable frame, and reports whether any variant is usable.
   * @returns {Promise<boolean>}
   */
  async #resolveAvailability() {
    await this.#resolveFrames(this.#variants);

    const container = this.refs.swatches;
    let anyAvailable = false;
    for (const variant of this.#variants) {
      if (variant.frameId) {
        anyAvailable = true;
      } else if (container instanceof HTMLElement) {
        const swatch = container.querySelector(`[data-variant-id="${CSS.escape(String(variant.id))}"]`);
        swatch?.setAttribute('aria-disabled', 'true');
      }
    }
    return anyAvailable;
  }

  /**
   * Sets each variant's `frameId` to the first candidate the viewer can actually load, or null, by
   * asking the adapter. Fault-tolerant: the adapter fails open, so on error every candidate counts
   * as available (the dialog still tries, matching the prior behavior). A timeout guards a slow API.
   * @param {VtoVariant[]} variants
   */
  async #resolveFrames(variants) {
    const adapter = this.#ensureAdapter();
    const candidates = variants.flatMap((variant) => variant.frameIds ?? []).filter(Boolean);

    const timeout = new Promise((resolve) => setTimeout(() => resolve(null), 4000));
    const resolved = await Promise.race([adapter.availableUids(candidates).catch(() => null), timeout]);
    // null = timeout or rejection → fail open: treat every candidate as available.
    const available = resolved ?? new Set(candidates);

    // In 3D mode a loadable frame is not enough: only frames digitized in 3D open in the viewer,
    // so narrow the resolved ids to the viewer-compatible ones. Free in practice — the flag rides
    // along on the findByApiKey responses the availability pass just cached. See VIS-599.
    let usable = available;
    if (this.#mode === 'viewer') {
      // Always a subset of `available`, since that is what it is resolved from.
      usable = await uidsWith('viewerCompatible', [...available], this.dataset.apiKey).catch(() => new Set());
    }

    for (const variant of variants) {
      variant.frameId = (variant.frameIds ?? []).find((id) => id && usable.has(id)) ?? null;
    }
  }

  /**
   * The related frames the native carousel offers (VIS-981): the product's Shopify "related"
   * recommendations, the same list as the PDP's "You may also like" section (Search & Discovery
   * picks first), rendered as payloads by sections/vto-recommendations.liquid, kept only where a
   * colour resolves to a frame. In 3D only frames with a 3D model count: the widget lists a
   * carousel frame without one and then shows an empty viewer for it.
   *
   * Resolved with the batch availability answer the launchers use (one request, cached for a day)
   * rather than the per-frame findByApiKey probe the product's own colours get: eight products'
   * colours would cost dozens of requests on every open, racing the product's own probe.
   * @param {VtoPayload['productId']} productId
   * @returns {Promise<VtoPayload[]>}
   */
  async #fetchRelated(productId) {
    if (!productId) return [];
    const url = new URL(Theme.routes.product_recommendations_url, location.origin);
    url.search = new URLSearchParams({
      intent: 'related',
      // The PDP's "You may also like" section asks for 8, so the carousel lists its frames.
      limit: '8',
      product_id: String(productId),
      section_id: 'vto-recommendations',
    }).toString();

    const response = await fetch(url);
    if (!response.ok) return [];
    const html = await response.text();
    const json = new DOMParser().parseFromString(html, 'text/html').querySelector('script[type="application/json"]');
    /** @type {VtoPayload[]} */
    const related = JSON.parse(json?.textContent || '[]');
    const variants = related.flatMap((product) => product.variants ?? []);
    const flag = this.#mode === 'viewer' ? 'viewerCompatible' : 'available';
    const usable = await uidsWith(flag, variants.flatMap((variant) => variant.frameIds ?? []), this.dataset.apiKey);
    for (const variant of variants) {
      variant.frameId = (variant.frameIds ?? []).find((id) => id && usable.has(String(id))) ?? null;
    }
    // The carousel shows at most 6 frames, and the one tried on is the first.
    return related.filter((product) => product.variants?.some((variant) => variant.frameId)).slice(0, 5);
  }

  /**
   * The Fittingbox native carousel: the product tried on first, then its related frames. The widget
   * renders no panel for a lone frame with a single colour (two frames, or one with two colours, is
   * the minimum), so that case keeps the dialog's own swatches.
   * @param {VtoPayload} payload
   * @param {VtoVariant | undefined} initial
   * @param {VtoPayload[]} related
   */
  #buildCarousel(payload, initial, related) {
    this.#productUrls.clear();
    // No key means the mock adapter, which draws no carousel: keep the swatch column.
    if (!initial || !this.dataset.apiKey) return null;

    const frames = [{ ...payload, variants: this.#variants }, ...related].map((product) => {
      // The panel names the selected colour after the frame's first variant, whatever `frame` the
      // widget started on, so the colour on screen leads.
      const variants = (product.variants ?? [])
        .filter((variant) => variant.frameId)
        .sort((a, b) => Number(b === initial) - Number(a === initial));
      for (const variant of variants) {
        const url = new URL(product.url ?? '', location.origin);
        url.searchParams.set('variant', String(variant.id));
        this.#productUrls.set(String(variant.frameId), url.toString());
      }
      return {
        frameLabel: product.modelLine,
        frameAdditionalInfo: product.brand,
        frameImageUrl: product.image || variants[0]?.heroImage,
        variants: variants.map((variant) => ({
          // Unique per frame: the widget tells the selected colour apart by its label alone, so two
          // "Montatura Nero" swatches would both show as selected.
          variantLabel: variantName(variant),
          variantThumbnailUrl: variant.swatchImage || product.image,
          // One size per colour: the payload collapses sizes, and no `sizeLabel` means no size tab.
          sizes: [{ frameId: variant.frameId }],
        })),
      };
    });
    if (frames.length < 2 && frames[0].variants.length < 2) return null;
    return { frames };
  }

  /** The first in-stock variant with a digitized frame, else the first digitized one. */
  #initialVariant() {
    const digitized = this.#variants.filter((variant) => variant.frameId);
    return digitized.find((variant) => variant.available) ?? digitized[0];
  }

  #renderSwatches() {
    const container = this.refs.swatches;
    if (!(container instanceof HTMLElement)) return;

    container.replaceChildren();
    for (const variant of this.#variants) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'vto-dialog__swatch';
      button.dataset.variantId = String(variant.id);
      button.setAttribute('role', 'option');
      // The visible color name lives in a single selected-color line (PDP-style, see
      // #selectVariant); each swatch carries the name only as its accessible label, with the color
      // code since macro colors can repeat across swatches (VIS-331).
      button.setAttribute('aria-label', variantName(variant));
      if (variant.swatchImage) button.style.setProperty('--vto-swatch-image', `url(${variant.swatchImage})`);
      container.append(button);
    }
  }

  /** @param {string | undefined} variantId */
  #selectVariant(variantId) {
    const variant = this.#variants.find((candidate) => String(candidate.id) === String(variantId));
    if (!variant) return;

    for (const swatch of this.refs.swatches?.querySelectorAll('[data-variant-id]') ?? []) {
      swatch.setAttribute('aria-selected', String(swatch.getAttribute('data-variant-id') === String(variantId)));
    }

    // Selected-color line under the heading (PDP-style): the single label updates on each selection.
    if (this.refs.colorValue instanceof HTMLElement) {
      this.refs.colorValue.textContent = variant.swatchLabel || variant.colorLabel;
    }

    // Model line: "<model> | <color_code> <color_name>" (mirrors blocks/product-model-color.liquid).
    if (this.refs.model instanceof HTMLElement) {
      const colorSegment = [variant.colorCode, variant.colorLabel].filter(Boolean).join(' ');
      this.refs.model.textContent = [this.#modelLine, colorSegment].filter(Boolean).join(' | ');
    }

    if (this.refs.hero instanceof HTMLImageElement && variant.heroImage) {
      this.refs.hero.src = variant.heroImage;
      this.refs.hero.alt = variant.colorLabel;
    }

    if (variant.frameId) this.#setFrame(variant.frameId);
  }

  /**
   * Serializes setFrame calls so rapid swatch switches don't drop the latest selection.
   * @param {string} frameId
   */
  #setFrame(frameId) {
    this.#frameQueue = this.#frameQueue.then(() => this.#ensureAdapter().setFrame(frameId)).catch(() => {});
    return this.#frameQueue;
  }

  /** @param {'consent' | 'requesting' | 'denied' | 'active' | 'loading' | 'unavailable'} state */
  #setState(state) {
    this.dataset.state = state;
  }
}

/**
 * A colour's name with its code: macro colours repeat across a product's swatches (VIS-331).
 * @param {VtoVariant} variant
 */
function variantName(variant) {
  return [variant.swatchLabel || variant.colorLabel, variant.colorCode].filter(Boolean).join(', ');
}

/** How long, from the click, the carousel's related frames may take before the dialog starts without. */
const RELATED_DEADLINE_MS = 3000;

if (!customElements.get('vto-dialog')) customElements.define('vto-dialog', VtoDialog);

// Open the shared dialog from any launcher on the page (delegated so PLP cards work too).
// Capture phase + stopPropagation stops any bubble-phase handlers; the gallery's click-to-zoom
// (also a capture-phase document listener) is neutralized at the source by the launcher button's
// own `on:click` shadow in vto-launcher.liquid — see the comment there.
document.addEventListener(
  'click',
  (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target);
    const trigger = target?.closest?.('[data-vto-launch]');
    if (!trigger) return;

    event.preventDefault();
    event.stopPropagation();

    const payload = trigger.closest('[data-vto-launcher]')?.querySelector('script[type="application/json"]')?.textContent;
    const dialog = document.getElementById('vto-dialog');
    if (!payload || !(dialog instanceof VtoDialog)) return;

    // Which of the two launchers was clicked: the try-on or the 3D view (VIS-599).
    const mode = /** @type {HTMLElement} */ (trigger).dataset.vtoLaunch === 'viewer' ? 'viewer' : 'live';

    try {
      // Opening also rescues a sibling button a failed probe left unanswered (hidden, not settled):
      // the dialog's own findByApiKey pass fills the shared cache with both flags, so the reveal
      // that follows costs nothing and no longer waits for the probe's pause to end.
      dialog.open(JSON.parse(payload), mode).finally(scheduleReveal);
    } catch {
      /* ignore malformed payloads */
    }
  },
  true
);

/**
 * Reveals the launchers whose frames Fittingbox can actually show: the try-on where it holds a
 * try-on asset, the 3D view where it holds a 3D model.
 *
 * Both buttons are rendered `hidden` (see snippets/vto-launcher.liquid): Shopify holds neither
 * flag, only Fittingbox knows, so the server cannot decide. One batch probe answers both flags for
 * every launcher on the page, a whole PLP grid in a single request, and the adapter's shared cache
 * makes every later pass free (later page views too, for a day). Failure leaves the buttons hidden
 * (fail closed): a launcher that opens onto nothing is worse than none.
 *
 * A button that can never be revealed is marked settled and skipped from then on, so a listing
 * full of frames without a try-on or a 3D model stops re-parsing every payload on every mutating
 * frame. The mark is an attribute rather than a WeakMap keyed by the button on purpose: morph
 * matches nodes by tag, so after a filter or pagination the same button can hold a different
 * product, and a cache keyed by the element would answer with the previous product's frames.
 * Morph strips any attribute its new markup lacks, so the mark disappears exactly when the product
 * can change. For the same reason a pass re-reads each payload after its lookup and leaves alone a
 * button whose product changed meanwhile: a filter tapped while the request is in flight would
 * otherwise get the old product's answer. The morph's own mutations queue the pass for the new one.
 */
async function revealLaunchers() {
  const buttons = [...document.querySelectorAll(`[data-vto-launch][hidden]:not([${SETTLED}])`)];
  if (!buttons.length) return;

  /** @type {Map<Element, { ids: string[], payload: string | undefined }>} */
  const candidates = new Map();
  for (const button of buttons) {
    const payload = payloadOf(button);
    let ids = [];
    try {
      const { variants = [] } = /** @type {VtoPayload} */ (JSON.parse(payload ?? ''));
      ids = variants.flatMap((variant) => variant.frameIds ?? []).filter(Boolean).map(String);
    } catch {
      /* missing or malformed payload: nothing to look up */
    }
    // No frame ids means nothing a later pass learns can reveal it, until a morph swaps the markup.
    if (ids.length) candidates.set(button, { ids, payload });
    else button.setAttribute(SETTLED, '');
  }
  if (!candidates.size) return;

  const apiKey = document.getElementById('vto-dialog')?.dataset.apiKey;
  const ids = [...candidates.values()].flatMap((candidate) => candidate.ids);
  // The first lookup caches both flags, so the second one sends no request of its own.
  const found = {
    available: await uidsWith('available', ids, apiKey),
    viewerCompatible: await uidsWith('viewerCompatible', ids, apiKey),
  };
  let unanswered = false;
  for (const [button, { ids: buttonIds, payload }] of candidates) {
    if (!button.isConnected || payloadOf(button) !== payload) continue; // product swapped meanwhile
    const flag = button.getAttribute('data-vto-launch') === 'viewer' ? 'viewerCompatible' : 'available';
    if (buttonIds.some((id) => found[flag].has(id))) button.removeAttribute('hidden');
    // Every id already answered "no". An id merely unanswered (a failure in backoff) stays
    // unsettled, so the pass after the pause can still reveal it.
    else if (knownWithout(flag, buttonIds)) button.setAttribute(SETTLED, '');
    else unanswered = true;
  }
  // Nothing else is sure to run that pass: on a quiet page no mutation follows, and the try-on
  // would stay hidden for the whole page view after one network blip. Once per page view, so an
  // endpoint that stays down is not asked every ten seconds for as long as the tab is open.
  if (unanswered && !retryScheduled && probeRetryIn() > 0) {
    retryScheduled = true;
    setTimeout(scheduleReveal, probeRetryIn() + 50);
  }
}

/** @param {Element} button @returns {string | undefined} The launcher's JSON payload text. */
function payloadOf(button) {
  return button.closest('[data-vto-launcher]')?.querySelector('script[type="application/json"]')?.textContent ?? undefined;
}

/** Whether the one retry after the probe's pause is already booked (see revealLaunchers). */
let retryScheduled = false;

// A morph re-applies the server markup's attributes to the nodes it reuses, so every revealed
// button goes back to `hidden` after a PLP filter, pagination or a predictive-search keystroke,
// and newly injected cards arrive hidden too. Same reasoning (and the same single-observer shape)
// as the wishlist toggle in assets/wishlist.js. The adapter cache means a re-reveal costs no
// request — but only once it is filled, which is why passes both coalesce into an animation frame
// and never overlap: a grid that renders progressively fires the observer repeatedly while the
// first lookup is still in flight, and each of those passes would otherwise re-request the very
// ids being fetched (measured: 13 requests for one 20-card listing instead of 1).
/** Marks a launcher button that no later pass can reveal (see revealLaunchers). */
const SETTLED = 'data-vto-settled';

let revealScheduled = false;
/** @type {Promise<void> | null} */
let revealRunning = null;
let revealQueued = false;

/** Queues a reveal pass: at most one per animation frame, and never two at once (see above). */
function scheduleReveal() {
  if (revealScheduled) return;
  revealScheduled = true;
  requestAnimationFrame(() => {
    revealScheduled = false;
    if (revealRunning) {
      revealQueued = true;
      return;
    }
    revealRunning = revealLaunchers().finally(() => {
      revealRunning = null;
      if (revealQueued) {
        revealQueued = false;
        scheduleReveal();
      }
    });
  });
}

if (typeof MutationObserver !== 'undefined') {
  new MutationObserver(scheduleReveal).observe(document.documentElement, {
    // The settled mark is watched too: a morph that strips it may leave the button's `hidden`
    // untouched and change only the payload's text, which neither of the other two report.
    attributeFilter: ['hidden', SETTLED],
    childList: true,
    subtree: true,
  });
}
scheduleReveal();
